import React, { useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { RootStore, RootStoreContext } from '../../stores/rootStore';
import ModalBase from '../layout/modal-base';
import { IForgotInputs, ILoginInputs, IRegisterInputs } from '../../models/dtoAuthTypes';
import { Form, Field, FieldRenderProps } from 'react-final-form';
import TextInput from '../forms/TextInput';
import { validateForgotForm, validateLoginRegisterForm } from '../forms/Validation';
import spinner from '../../assets/images/Spinner-1s-55px.gif';

const ModalAuth: React.FC = () => {
    const rootStore: RootStore = useContext(RootStoreContext);

    const { isLoginFormOpened, OpenLoginForm, CloseLoginForm, Login, loginError } = rootStore.userStore;
    const { isForgotFormOpened, OpenForgotForm, CloseForgotForm, RequestReset, forgotError } = rootStore.userStore;
    const { isRegisterFormOpened, OpenRegisterForm, CloseRegisterForm, Register, registerError } = rootStore.userStore;
    const { isLoggingIn: isConnecting } = rootStore.userStore;

    const handleLoginFormSubmit = async (values: ILoginInputs) => { Login(values); };
    const handleForgotFormSubmit = async (values: IForgotInputs) => { await RequestReset(values); };
    const handleRegisterFormSubmit = async (values: IRegisterInputs) => { Register(values); };

    return (
        <>
            {isLoginFormOpened && (
                <ModalBase close={CloseLoginForm} title="Sign in">
                    <p className="dcr-modal-sub">Access your subscriptions and account</p>
                    <Form
                        onSubmit={handleLoginFormSubmit}
                        validate={validateLoginRegisterForm}
                        render={({ handleSubmit, submitting, invalid }) => (
                            <form onSubmit={handleSubmit}>
                                {loginError && <p className="dcr-error">{loginError}</p>}
                                <div className="dcr-field">
                                    <label>Email</label>
                                    <Field
                                        name="email"
                                        component={TextInput as React.ComponentType<FieldRenderProps<string, HTMLElement, string>>}
                                        type="email"
                                        placeholder="you@email.com"
                                    />
                                </div>
                                <div className="dcr-field">
                                    <label>Password</label>
                                    <Field
                                        name="password"
                                        component={TextInput as React.ComponentType<FieldRenderProps<string, HTMLElement, string>>}
                                        type="password"
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div className="dcr-row">
                                    <Field
                                        name="remeberMe"
                                        type="checkbox"
                                        render={({ input }: FieldRenderProps<boolean, HTMLElement>) => (
                                            <label>
                                                <input {...input} type="checkbox" value={input.value ? 'true' : 'false'} />
                                                Remember me
                                            </label>
                                        )}
                                    />
                                    <a href="" onClick={e => { e.preventDefault(); OpenForgotForm(); }}>Forgot password?</a>
                                </div>
                                <div className="dcr-actions">
                                    {isConnecting ? (
                                        <img src={spinner} alt="Loading…" style={{ height: '36px' }} />
                                    ) : (
                                        <>
                                            <button type="submit" className="dcr-btn dcr-btn-primary" disabled={submitting || invalid}>Sign in</button>
                                            <button type="button" className="dcr-btn dcr-btn-outline" onClick={CloseLoginForm}>Cancel</button>
                                        </>
                                    )}
                                </div>
                            </form>
                        )}
                    />
                    <div className="dcr-newcust">
                        New here?{' '}
                        <a href="" onClick={e => { e.preventDefault(); OpenRegisterForm(); }}>Create an account</a>
                    </div>
                </ModalBase>
            )}

            {isForgotFormOpened && (
                <ModalBase close={CloseForgotForm} title="Forgot password">
                    <p className="dcr-modal-sub">Enter your email and we'll send a reset link</p>
                    <Form
                        onSubmit={handleForgotFormSubmit}
                        validate={validateForgotForm}
                        render={({ handleSubmit, invalid, submitting }) => (
                            <form onSubmit={handleSubmit}>
                                {forgotError && <p className="dcr-error">{forgotError}</p>}
                                <div className="dcr-field">
                                    <label>Email</label>
                                    <Field
                                        name="email"
                                        component={TextInput as React.ComponentType<FieldRenderProps<string, HTMLElement, string>>}
                                        type="email"
                                        placeholder="you@email.com"
                                    />
                                </div>
                                <div className="dcr-actions">
                                    {isConnecting ? (
                                        <img src={spinner} alt="Loading…" style={{ height: '36px' }} />
                                    ) : (
                                        <>
                                            <button type="submit" className="dcr-btn dcr-btn-primary" disabled={submitting || invalid}>Send reset link</button>
                                            <button type="button" className="dcr-btn dcr-btn-outline" onClick={CloseForgotForm}>Cancel</button>
                                        </>
                                    )}
                                </div>
                            </form>
                        )}
                    />
                    <div className="dcr-newcust">
                        <a href="" onClick={e => { e.preventDefault(); OpenLoginForm(); }}>Back to sign in</a>
                    </div>
                </ModalBase>
            )}

            {isRegisterFormOpened && (
                <ModalBase close={CloseRegisterForm} title="Create account">
                    <p className="dcr-modal-sub">Get access to your subscriptions</p>
                    <Form
                        onSubmit={handleRegisterFormSubmit}
                        validate={validateLoginRegisterForm}
                        render={({ handleSubmit, submitting, invalid }) => (
                            <form onSubmit={handleSubmit}>
                                {registerError && <p className="dcr-error">{registerError}</p>}
                                <div className="dcr-field">
                                    <label>Email</label>
                                    <Field
                                        name="email"
                                        component={TextInput as React.ComponentType<FieldRenderProps<string, HTMLElement, string>>}
                                        type="email"
                                        placeholder="you@email.com"
                                    />
                                </div>
                                <div className="dcr-field">
                                    <label>Password</label>
                                    <Field
                                        name="password"
                                        component={TextInput as React.ComponentType<FieldRenderProps<string, HTMLElement, string>>}
                                        type="password"
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div className="dcr-actions">
                                    {isConnecting ? (
                                        <img src={spinner} alt="Loading…" style={{ height: '36px' }} />
                                    ) : (
                                        <>
                                            <button type="submit" className="dcr-btn dcr-btn-primary" disabled={submitting || invalid}>Create account</button>
                                            <button type="button" className="dcr-btn dcr-btn-outline" onClick={CloseRegisterForm}>Cancel</button>
                                        </>
                                    )}
                                </div>
                            </form>
                        )}
                    />
                    <div className="dcr-newcust">
                        Already have an account?{' '}
                        <a href="" onClick={e => { e.preventDefault(); OpenLoginForm(); }}>Sign in</a>
                    </div>
                </ModalBase>
            )}
        </>
    );
};

export default observer(ModalAuth);
